import { PublicClientApplication, type AuthError } from '@azure/msal-browser';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
// eslint-disable-next-line no-restricted-imports
import { Box, Button, createStyles, Stack, Tabs, Text } from '@mantine/core';
import { useSuspenseApiMemberEnv } from '@repo/api-codegen';
import type { IEmbedConfiguration } from 'powerbi-client';
import { factories, models, service } from 'powerbi-client';
import { useEffect, useMemo, useRef, useState } from 'react';
import IntegrationLogo from '../IntegrationLogo';

const powerbi = new service.Service(
	factories.hpmFactory,
	factories.wpmpFactory,
	factories.routerFactory
);

interface PowerBiReport {
	title: string;
	id: string;
	embedUrl: string;
	embedToken?: string;
	embedTokenExpiration?: string;
}

interface PowerBiPreviewProps {
	previewUrl: string;
	previewRef?: React.RefObject<HTMLDivElement>;
	embedToken?: string;
	embedTokenExpiration?: string;
}

const useStyles = createStyles((theme) => ({
	tabsContainer: {
		marginBottom: theme.spacing.md,
	},
	reportContainer: {
		flex: 1,
		overflow: 'hidden',
	},
}));

function PowerBiPreviewImpl({
	previewUrl,
	previewRef,
	embedToken,
	embedTokenExpiration,
}: PowerBiPreviewProps) {
	const { instance, accounts, inProgress } = useMsal();
	const [accessToken, setAccessToken] = useState<string>('');
	const [, setError] = useState<string[]>([]);
	const internalRef = useRef<HTMLDivElement>(null);
	const reportRef = previewRef || internalRef;
	let isDashboard = previewUrl.includes('dashboardEmbed');

	// If we have an embed token, skip AAD authentication
	const hasEmbedToken = !!embedToken;

	useEffect(() => {
		// Skip authentication if we have an embed token
		if (hasEmbedToken) {
			return;
		}

		const authenticate = async () => {
			const isAuthenticated = accounts.length > 0;

			const scopeBase = [
				'https://analysis.windows.net/powerbi/api/Dashboard.Read.All',
				'https://analysis.windows.net/powerbi/api/Report.Read.All',
			];

			const tokenRequest = {
				scopes: scopeBase,
				account: accounts[0],
			};

			if (isAuthenticated && !accessToken) {
				try {
					const response = await instance.acquireTokenSilent(tokenRequest);
					setAccessToken(response.accessToken);
				} catch (error) {
					const authError = error as AuthError;
					if (
						authError.errorCode === 'consent_required' ||
						authError.errorCode === 'interaction_required' ||
						authError.errorCode === 'login_required'
					) {
						instance.acquireTokenPopup(tokenRequest);
					} else {
						setError([
							'Problem fetching access token: ' + authError.toString(),
						]);
					}
				}
			}
		};

		authenticate();
	}, [instance, accounts, inProgress, accessToken, isDashboard, hasEmbedToken]);

	useEffect(() => {
		if (reportRef?.current && previewUrl) {
			const urlPrefix = isDashboard
				? 'https://app.powerbi.com/dashboardEmbed?dashboardId='
				: 'https://app.powerbi.com/reportEmbed?reportId=';
			const entityId = previewUrl.substring(
				urlPrefix.length,
				urlPrefix.length + 36 // GUID length is 36 chars
			);

			// Use embed token if available, otherwise use AAD token
			if (hasEmbedToken && embedToken) {
				const embedConfiguration: IEmbedConfiguration = {
					type: isDashboard ? 'dashboard' : 'report',
					tokenType: models.TokenType.Embed,
					accessToken: embedToken,
					embedUrl: previewUrl,
					id: entityId,
					pageView: 'fitToWidth',
				};

				powerbi.embed(reportRef.current, embedConfiguration);
			} else if (accessToken) {
				const embedConfiguration: IEmbedConfiguration = {
					type: isDashboard ? 'dashboard' : 'report',
					tokenType: models.TokenType.Aad,
					accessToken,
					embedUrl: previewUrl,
					id: entityId,
					pageView: 'fitToWidth',
				};

				powerbi.embed(reportRef.current, embedConfiguration);
			}
		}
	}, [
		accessToken,
		embedToken,
		hasEmbedToken,
		isDashboard,
		previewUrl,
		reportRef,
	]);

	const loginScopeBase = [
		'https://analysis.windows.net/powerbi/api/Dashboard.Read.All',
		'https://analysis.windows.net/powerbi/api/Report.Read.All',
	];

	const loginRequest = {
		scopes: loginScopeBase,
		account: accounts[0],
	};

	// Show the embed container if we have an embed token OR if the user is authenticated
	if ((hasEmbedToken || useIsAuthenticated()) && previewUrl) {
		return (
			<Box
				ref={reportRef}
				w="100%"
				h="100%"
				sx={{ overflow: 'hidden', position: 'relative' }}
			/>
		);
	} else {
		return (
			<Stack align="center" justify="center" h="100%" spacing="md">
				<IntegrationLogo integrationType={'powerbi'} size={60} />
				<Button
					variant="primary"
					onClick={() => instance.loginPopup(loginRequest)}
					size="xl"
				>
					Sign in
				</Button>
			</Stack>
		);
	}
}

function PowerBiAppWrapper({ previewUrl, previewRef }: PowerBiPreviewProps) {
	const { classes } = useStyles();

	// Check if this is a PowerBI app URL (non-embeddable)
	const isAppUrl = useMemo(
		() => previewUrl.includes('/groups/me/apps/'),
		[previewUrl]
	);

	// Parse PowerBI data - can be reports array OR single report with embed token
	const powerBiReports = useMemo(() => {
		try {
			const parsed = JSON.parse(previewUrl);

			// Check if it's an array of reports (for apps)
			if (Array.isArray(parsed) && parsed.length > 0) {
				return parsed as PowerBiReport[];
			}
		} catch {
			// Not JSON, fallback to regular preview
		}
		return null;
	}, [previewUrl]);

	// Parse single report with embed token
	const singleReportData = useMemo(() => {
		try {
			const parsed = JSON.parse(previewUrl);

			// Check if it's a single report object with embedToken
			if (
				parsed &&
				!Array.isArray(parsed) &&
				parsed.embedUrl &&
				parsed.embedToken
			) {
				return parsed as {
					embedUrl: string;
					embedToken: string;
					embedTokenExpiration?: string;
				};
			}
		} catch {
			// Not JSON, fallback to regular preview
		}
		return null;
	}, [previewUrl]);

	// Initialize activeTab with the first report's id directly
	const [activeTab, setActiveTab] = useState<string>(
		() => powerBiReports?.[0]?.id || ''
	);

	// Get current embed data based on selected tab
	const currentReportData = useMemo(() => {
		if (powerBiReports && activeTab) {
			const selectedReport = powerBiReports.find(
				(report) => report.id === activeTab
			);
			return selectedReport || null;
		}
		return null;
	}, [powerBiReports, activeTab]);

	// Handle PowerBI app URL (non-embeddable)
	if (isAppUrl) {
		const fullUrl = previewUrl.startsWith('http')
			? previewUrl
			: `https://${previewUrl}`;

		return (
			<Stack align="center" justify="center" h="100%" spacing="md">
				<IntegrationLogo integrationType={'powerbi'} size={60} />
				<Button
					variant="primary"
					onClick={() => window.open(fullUrl, '_blank')}
					size="xl"
				>
					Open App in Power BI
				</Button>
			</Stack>
		);
	}

	// Handle single report with embed token
	if (singleReportData) {
		return (
			<PowerBiPreviewImpl
				previewUrl={singleReportData.embedUrl}
				previewRef={previewRef}
				embedToken={singleReportData.embedToken}
				embedTokenExpiration={singleReportData.embedTokenExpiration}
			/>
		);
	}

	if (!powerBiReports || powerBiReports.length === 0) {
		// Not a PowerBI app, use regular preview
		return (
			<PowerBiPreviewImpl previewUrl={previewUrl} previewRef={previewRef} />
		);
	}

	return (
		<Stack h="100%" spacing={0}>
			<Box className={classes.tabsContainer}>
				<Tabs
					value={activeTab}
					onTabChange={(value) => setActiveTab(value || '')}
					variant="pills"
				>
					<Tabs.List>
						{powerBiReports.map((report) => (
							<Tabs.Tab key={report.id} value={report.id}>
								{report.title.replace('[App] ', '')}
							</Tabs.Tab>
						))}
					</Tabs.List>
				</Tabs>
			</Box>
			<Box className={classes.reportContainer}>
				{currentReportData?.embedUrl ? (
					<PowerBiPreviewImpl
						previewUrl={currentReportData.embedUrl}
						embedToken={currentReportData.embedToken}
						embedTokenExpiration={currentReportData.embedTokenExpiration}
					/>
				) : (
					<Text>No embed URL available</Text>
				)}
			</Box>
		</Stack>
	);
}

export default function PowerBiPreview({
	previewUrl,
	previewRef,
}: PowerBiPreviewProps) {
	const { data: memberEnv } = useSuspenseApiMemberEnv({});

	const POWERBI_CLIENT_ID = memberEnv?.POWERBI_CLIENT_ID;
	const POWERBI_TENANT_ID = memberEnv?.POWERBI_TENANT_ID;

	const onPremMsal = useMemo(() => {
		if (!POWERBI_TENANT_ID || !POWERBI_CLIENT_ID) {
			return null;
		}

		const msalConfig = {
			auth: {
				clientId: POWERBI_CLIENT_ID,
				authority: `https://login.microsoftonline.com/${POWERBI_TENANT_ID}`,
				redirectUri: `${window.location.origin}/auth/callback`,
				navigateToLoginRequestUrl: false,
			},
			cache: {
				cacheLocation: 'sessionStorage',
				storeAuthStateInCookie: false,
			},
		};

		return new PublicClientApplication(msalConfig);
	}, [POWERBI_CLIENT_ID, POWERBI_TENANT_ID]);

	if (!onPremMsal) {
		// Use Managed MSAL Auth
		return (
			<PowerBiAppWrapper previewUrl={previewUrl} previewRef={previewRef} />
		);
	}

	return (
		<MsalProvider instance={onPremMsal}>
			<PowerBiAppWrapper previewUrl={previewUrl} previewRef={previewRef} />
		</MsalProvider>
	);
}
